import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { X, FileText, Tag, Plus, Trash2 } from "lucide-react";
import { CATEGORY_LABELS } from "../lib/categories";
import { groupByCategory } from "../lib/sections";

const TAX_RATE_DEFAULT = 0.0;
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

/**
 * Admin-only modal: Review & Issue Invoice.
 *
 * Feb 2026 client rule: **no sales tax** on franchisee invoices. The tax
 * field stays on the model for future use, but the editor no longer shows
 * a tax input and always sends tax_rate=0. Subtotal − discount = total.
 *
 * Packaging line prices default to 0 (no permanent price) — admin fills
 * them in per-invoice by editing the Unit $ column.
 */
/**
 * Modal for reviewing/editing an invoice.
 *
 *   - `order` prop: create a brand-new invoice from a picked-up/pulled order.
 *   - `invoice` prop: edit an existing DRAFT invoice (e.g. one auto-created
 *     when the order hit Ready-for-Pickup) — packaging prices, discount, notes.
 *
 * Exactly one of the two props must be provided. Save routes to POST /invoices
 * for the create flow and PATCH /invoices/{id} for the edit flow. Tax is
 * always 0.
 */
export default function InvoiceEditorDialog({ order, invoice, manual = false, onClose, onIssued }) {
  const editingDraft = !!invoice;
  const isSentInvoice = editingDraft && invoice?.status && invoice.status !== "draft";
  const [manualSource] = useState({ id: "manual", items: [] });
  const source = manual ? manualSource : (order || (invoice ? { id: invoice.order_id, order_no: invoice.order_no, items: invoice.items } : null));
  const [locations, setLocations] = useState([]);
  const [locId, setLocId] = useState("");
  useEffect(() => {
    if (manual) api.get("/locations").then((r) => setLocations(r.data.filter((l) => l.name !== "Kitchen")));
  }, [manual]);
  const [catalogList, setCatalogList] = useState([]);    // array of {id, name, category, price}
  const [catalog, setCatalog] = useState({});            // {item_id: price} for backfill
  const [lines, setLines] = useState([]);
  const [discount, setDiscount] = useState("");
  const [discountLabel, setDiscountLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // "+ Add line" picker — either an inventory item or a custom name
  const [addMode, setAddMode] = useState("inventory"); // inventory | custom
  const [newItemId, setNewItemId] = useState("");
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState(1);
  const [newPrice, setNewPrice] = useState("");

  useEffect(() => {
    if (!source) return;
    setErr("");
    setDiscount(editingDraft && invoice.discount_amount ? String(invoice.discount_amount) : "");
    setDiscountLabel(editingDraft ? (invoice.discount_label || "") : "");
    setNotes(editingDraft ? (invoice.notes || "") : "");
    Promise.all([api.get("/inventory"), api.get("/inventory/order-catalog")]).then(([r, cat]) => {
      const map = {};
      for (const i of r.data) map[i.id] = i.price ?? 0;
      const list = cat.data.map((i) => ({ id: i.id, name: i.name, category: i.category, price: i.price ?? 0 }));
      list.sort((a, b) => a.name.localeCompare(b.name));
      setCatalog(map);
      setCatalogList(list);
      setLines(
        source.items.map((it) => ({
          item_id: it.item_id,
          name: it.name,
          category: it.category,
          isCustom: typeof it.item_id === "string" && it.item_id.startsWith("custom:"),
          quantity: editingDraft
            ? (it.billed_quantity ?? it.quantity)
            : (it.fulfilled_quantity ?? it.quantity),
          unit_price: editingDraft
            ? (it.unit_price ?? 0)
            : (map[it.item_id] ?? it.unit_price ?? 0),
        })),
      );
    });
    // Depend on STABLE ids only — `source` is a new object on every render,
    // which previously re-ran this effect after every auto-save and wiped
    // the just-added line back to the invoice's original items.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, invoice?.id, manual]);

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0),
    [lines],
  );
  const discountAmt = Math.max(0, parseFloat(discount) || 0);
  const total = Math.max(0, subtotal - discountAmt);

  // Push the current `lines` state to the backend. Used for the auto-save
  // on + Add and on × Remove when editing an existing invoice so the admin
  // sees the line reflected on the bill immediately without having to click
  // Save Draft afterwards. For the create flow (editingDraft === false) we
  // don't auto-save because the invoice doesn't exist yet.
  const persistLines = async (nextLines) => {
    if (!editingDraft) return;
    try {
      const items = nextLines.map((l) => ({
        item_id: l.item_id, name: l.name, category: l.category,
        quantity: parseInt(l.quantity, 10) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
      })).filter((it) => it.name && it.quantity > 0);
      if (items.length === 0) return;  // can't send empty — Save button will handle this edge
      const res = await api.patch(`/invoices/${invoice.id}`, {
        items,
        discount_amount: discountAmt,
        discount_label: discountLabel || null,
        notes: notes || null,
      });
      // Reflect any backend-added fields (e.g. custom:<uuid> for new custom lines)
      setLines(res.data.items.map((it) => ({
        item_id: it.item_id, name: it.name, category: it.category,
        isCustom: typeof it.item_id === "string" && it.item_id.startsWith("custom:"),
        quantity: it.billed_quantity ?? it.quantity,
        unit_price: it.unit_price ?? 0,
      })));
      onIssued?.(res.data);  // refresh parent invoices list
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const updateLine = (idx, patch) => {
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const removeLine = (idx) => {
    const next = lines.filter((_, i) => i !== idx);
    setLines(next);
    if (editingDraft && next.length > 0) persistLines(next);
  };
  const addLine = () => {
    let next;
    if (addMode === "inventory") {
      if (!newItemId) return setErr("Pick an item from the catalog first");
      const inv = catalogList.find((c) => c.id === newItemId);
      if (!inv) return;
      const existing = lines.findIndex((l) => l.item_id === newItemId);
      if (existing >= 0) {
        next = lines.map((l, i) => i === existing
          ? { ...l, quantity: (l.quantity || 0) + (parseInt(newQty, 10) || 1) }
          : l);
      } else {
        next = [...lines, {
          item_id: inv.id, name: inv.name, category: inv.category,
          isCustom: false,
          quantity: Math.max(1, parseInt(newQty, 10) || 1),
          unit_price: newPrice !== "" ? Math.max(0, parseFloat(newPrice) || 0) : inv.price,
        }];
      }
    } else {
      if (!newName.trim()) return setErr("Type a name for the custom line");
      next = [...lines, {
        item_id: null,
        name: newName.trim(), category: "other",
        isCustom: true,
        quantity: Math.max(1, parseInt(newQty, 10) || 1),
        unit_price: Math.max(0, parseFloat(newPrice) || 0),
      }];
    }
    setLines(next);
    setErr("");
    setNewItemId(""); setNewName(""); setNewQty(1); setNewPrice("");
    // Auto-save so the admin sees the new line on the bill immediately,
    // without having to click Save Draft every single click.
    if (editingDraft) {
      persistLines(next).then(() => {
        toast.success(`Added · line saved to ${invoice.invoice_no}`);
      });
    }
  };

  const issue = async () => {
    setBusy(true);
    setErr("");
    try {
      let data;
      if (manual) {
        if (!locId) { setErr("Pick which franchise this invoice is for"); setBusy(false); return; }
        const res = await api.post("/invoices/manual", {
          location_id: locId,
          items: lines.map((l) => ({
            item_id: l.item_id, name: l.name, category: l.category,
            quantity: parseInt(l.quantity, 10) || 0, unit_price: parseFloat(l.unit_price) || 0,
          })),
          discount_amount: discountAmt, discount_label: discountLabel || null, notes: notes || null,
        });
        data = res.data;
        toast.success(`Invoice ${data.invoice_no} — Draft created`, {
          description: `${fmt(data.total)} — click "Send" in the invoice list to deliver to the franchisee.`,
        });
      } else if (editingDraft) {
        // Full replacement mode — supports add + remove + rename
        const items = lines.map((l) => ({
          item_id: l.item_id, name: l.name, category: l.category,
          quantity: parseInt(l.quantity, 10) || 0,
          unit_price: parseFloat(l.unit_price) || 0,
        }));
        const res = await api.patch(`/invoices/${invoice.id}`, {
          items,
          discount_amount: discountAmt,
          discount_label: discountLabel || null,
          notes: notes || null,
        });
        data = res.data;
        if (isSentInvoice) {
          toast.success(`Invoice ${data.invoice_no} — Updated`, {
            description: `New total ${fmt(data.total)} — franchisee has been notified.`,
          });
        } else {
          toast.success(`Invoice ${data.invoice_no} — Saved`, {
            description: `${fmt(data.total)} — click "Send" in the invoice list to deliver to the franchisee.`,
          });
        }
      } else {
        // Create flow — order-based lines + optional extra lines admin added
        const unit_prices = {};
        const billed_quantities = {};
        const extra_items = [];
        const orderLineIds = new Set((order?.items || []).map((it) => it.item_id));
        for (const l of lines) {
          if (l.item_id && orderLineIds.has(l.item_id)) {
            unit_prices[l.item_id] = parseFloat(l.unit_price) || 0;
            billed_quantities[l.item_id] = parseInt(l.quantity, 10) || 0;
          } else {
            // Added by admin — treat as an extra line
            extra_items.push({
              item_id: l.item_id || null,
              name: l.name, category: l.category,
              quantity: parseInt(l.quantity, 10) || 0,
              unit_price: parseFloat(l.unit_price) || 0,
            });
          }
        }
        // Any order line REMOVED by admin → set its billed_qty to 0
        for (const it of (order?.items || [])) {
          if (!lines.some((l) => l.item_id === it.item_id)) {
            billed_quantities[it.item_id] = 0;
            unit_prices[it.item_id] = 0;
          }
        }
        const res = await api.post("/invoices", {
          order_id: order.id,
          unit_prices, billed_quantities, extra_items,
          discount_amount: discountAmt,
          discount_label: discountLabel || null,
          tax_rate: 0,
          notes: notes || null,
        });
        data = res.data;
        toast.success(`Invoice ${data.invoice_no} — Draft created`, {
          description: `${fmt(data.total)} — click "Send" in the invoice list to deliver to the franchisee.`,
        });
      }
      onIssued?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!source) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="invoice-editor-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-2xl border border-border w-full max-w-3xl max-h-[92vh] flex flex-col"
        data-testid="invoice-editor"
      >
        {/* Header */}
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5">
              <FileText className="h-3 w-3" />
              {manual ? "New Custom Invoice" : editingDraft
                ? (isSentInvoice ? "Edit Invoice" : "Edit Draft Invoice")
                : "Review & Issue Invoice"}
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight">
              {manual ? "Custom Invoice" : editingDraft ? invoice.invoice_no : order.order_no}
            </h2>
            {manual ? (
              <select value={locId} onChange={(e) => setLocId(e.target.value)} data-testid="manual-invoice-location"
                className="mt-2 h-10 rounded-lg border border-input bg-card px-3 text-sm">
                <option value="">Bill to which franchise…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            ) : (
            <p className="text-sm text-muted-foreground mt-1">
              {editingDraft
                ? `${invoice.location_name}${invoice.customer ? ` — ${invoice.customer}` : ""} · order ${invoice.order_no}`
                : `${order.customer || order.location_name}${order.pickup_date ? ` · pickup ${order.pickup_date}` : ""}`}
            </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Line items table */}
          <div>
            <div className="label-eyebrow mb-2">Line items</div>
            <div className="grid grid-cols-[1fr_72px_96px_96px_32px] gap-2 px-2 py-2 label-eyebrow text-[10px] border-b border-border">
              <div>Item</div>
              <div className="text-center">Qty</div>
              <div className="text-right">Unit $</div>
              <div className="text-right">Line</div>
              <div />
            </div>
            <div className="divide-y divide-border">
              {groupByCategory(lines).map((g) => (
              <div key={g.cat} data-testid={`invoice-editor-section-${g.cat}`}>
                <div className="px-2 pt-3 pb-1 label-eyebrow text-[10px] text-[hsl(var(--primary))]">{g.label}</div>
              {g.items.map((l) => {
                const idx = l._idx;
                const line = (l.quantity || 0) * (l.unit_price || 0);
                return (
                  <div
                    key={`${l.item_id || "custom"}-${idx}`}
                    className="grid grid-cols-[1fr_72px_96px_96px_32px] gap-2 px-2 py-2.5 items-center"
                    data-testid={`invoice-line-${l.item_id || `custom-${idx}`}`}
                  >
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      <span className="truncate">{l.name}</span>
                      {l.category && l.category !== "other" && (
                        <span className="text-[11px] font-normal text-muted-foreground whitespace-nowrap">— {CATEGORY_LABELS[l.category] || l.category}</span>
                      )}
                      {l.isCustom && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-secondary border border-border text-muted-foreground">Custom</span>
                      )}
                    </div>
                    <input
                      type="number" min="0"
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                      data-testid={`invoice-qty-${l.item_id || `custom-${idx}`}`}
                      className="h-9 w-full rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums"
                    />
                    <input
                      type="number" min="0" step="0.01"
                      value={l.unit_price}
                      onChange={(e) => updateLine(idx, { unit_price: Math.max(0, parseFloat(e.target.value) || 0) })}
                      data-testid={`invoice-price-${l.item_id || `custom-${idx}`}`}
                      className="h-9 w-full rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
                    />
                    <div className="text-right text-sm tabular-nums">{fmt(line)}</div>
                    <button
                      onClick={() => removeLine(idx)}
                      aria-label={`Remove ${l.name}`}
                      data-testid={`invoice-remove-${l.item_id || `custom-${idx}`}`}
                      className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 flex items-center justify-center"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
                <div className="grid grid-cols-[1fr_72px_96px_96px_32px] gap-2 px-2 py-2 rounded-lg bg-secondary/50 text-xs font-medium" data-testid={`invoice-editor-section-total-${g.cat}`}>
                  <div>{g.label} total</div>
                  <div className="text-center tabular-nums">{g.items.reduce((t, l) => t + (parseInt(l.quantity, 10) || 0), 0)}</div>
                  <div />
                  <div className="text-right tabular-nums">{fmt(g.items.reduce((t, l) => t + (l.quantity || 0) * (l.unit_price || 0), 0))}</div>
                  <div />
                </div>
              </div>
              ))}
              {lines.length === 0 && (
                <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                  All lines removed. Add at least one line below before saving.
                </div>
              )}
            </div>

            {/* Add-line row (admin can add inventory items or free-text lines
                throughout the whole invoice lifecycle, draft or outstanding) */}
            <div className="mt-3 rounded-xl border border-dashed border-border bg-secondary/30 p-3 space-y-2" data-testid="invoice-add-line">
              <div className="flex items-center gap-2 text-[11px]">
                <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="label-eyebrow">Add line</span>
                <div className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={() => { setAddMode("inventory"); setErr(""); }}
                    data-testid="invoice-add-mode-inventory"
                    className={`px-2 py-1 rounded-md text-[11px] ${addMode === "inventory" ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground"}`}
                  >Inventory</button>
                  <button
                    type="button"
                    onClick={() => { setAddMode("custom"); setErr(""); }}
                    data-testid="invoice-add-mode-custom"
                    className={`px-2 py-1 rounded-md text-[11px] ${addMode === "custom" ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground"}`}
                  >Custom</button>
                </div>
              </div>
              <div className="grid grid-cols-[1fr_60px_80px_auto] gap-2 items-center">
                {addMode === "inventory" ? (
                  <select
                    value={newItemId}
                    onChange={(e) => {
                      setNewItemId(e.target.value);
                      const inv = catalogList.find((c) => c.id === e.target.value);
                      if (inv && newPrice === "") setNewPrice(String(inv.price || 0));
                    }}
                    data-testid="invoice-add-item-select"
                    className="h-9 rounded-lg border border-input bg-card px-2 text-sm"
                  >
                    <option value="">Pick inventory item…</option>
                    {catalogList.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.category && `· ${CATEGORY_LABELS[c.category] || c.category}`} {c.price ? ` · $${c.price.toFixed(2)}` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text" value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Custom line name (e.g. Delivery fee)"
                    data-testid="invoice-add-name"
                    className="h-9 rounded-lg border border-input bg-card px-2 text-sm"
                  />
                )}
                <input
                  type="number" min="1" value={newQty}
                  onChange={(e) => setNewQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  placeholder="Qty"
                  data-testid="invoice-add-qty"
                  className="h-9 rounded-lg border border-input bg-card px-2 text-center text-sm tabular-nums"
                />
                <input
                  type="number" min="0" step="0.01" value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="Unit $"
                  data-testid="invoice-add-price"
                  className="h-9 rounded-lg border border-input bg-card px-2 text-right text-sm tabular-nums"
                />
                <button
                  type="button"
                  onClick={addLine}
                  data-testid="invoice-add-line-btn"
                  className="h-9 px-3 rounded-lg bg-foreground text-background text-xs font-medium hover:bg-foreground/90 inline-flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
            </div>
          </div>

          {/* Discount */}
          <div>
            <label className="label-eyebrow mb-1.5 block inline-flex items-center gap-1">
              <Tag className="h-3 w-3" /> Discount
            </label>
            <div className="flex gap-2">
              <input
                type="number" min="0" step="0.01"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="0.00"
                data-testid="invoice-discount-amt"
                className="h-10 w-28 rounded-xl border border-input bg-card px-3 text-right text-sm tabular-nums"
              />
              <input
                type="text"
                value={discountLabel}
                onChange={(e) => setDiscountLabel(e.target.value)}
                placeholder="Label (e.g. Loyalty 10%)"
                data-testid="invoice-discount-label"
                className="h-10 flex-1 rounded-xl border border-input bg-card px-3 text-sm"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label-eyebrow mb-1.5 block">Internal notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional — appears on the invoice"
              data-testid="invoice-notes"
              className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm"
            />
          </div>

          {/* Totals */}
          <div className="rounded-2xl border border-border bg-secondary/30 p-5 space-y-1.5">
            <Row label="Subtotal" value={fmt(subtotal)} />
            {discountAmt > 0 && (
              <Row label={`Discount${discountLabel ? ` · ${discountLabel}` : ""}`} value={`− ${fmt(discountAmt)}`} muted />
            )}
            <div className="h-px bg-border my-2" />
            <Row label="Total" value={fmt(total)} big />
          </div>

          {err && (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {manual ? "Saved as a draft — send it from the invoice list when ready." : "Once issued, the franchisee will be notified instantly."}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="h-10 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={issue}
              disabled={busy || total <= 0}
              data-testid="invoice-issue-btn"
              className="h-10 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-60 inline-flex items-center gap-2"
            >
              {busy ? "Saving…" : manual ? `Create Draft · ${fmt(total)}` : editingDraft
                ? (isSentInvoice ? `Save · ${fmt(total)}` : `Save Draft · ${fmt(total)}`)
                : `Issue Invoice · ${fmt(total)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, big }) {
  return (
    <div className={`flex justify-between text-sm ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span className={`tabular-nums ${big ? "font-display text-2xl font-light" : ""}`}>{value}</span>
    </div>
  );
}
